import React from "react";
import { ArrowUpRight, ArrowDownRight, Send, Mail } from "lucide-react";
import { useApp } from "@/context/AppContext";

export default function SignalBox({ signal, onNotify, notifying, instScore }) {
  const { t } = useApp();
  if (!signal) {
    return (
      <div className="panel p-5" data-testid="signal-box-empty">
        <div className="panel-title text-[14px] mb-3">{t("signal.title", "Signal actuel")}</div>
        <div className="text-sm text-white/50">{t("signal.no_signal", "Aucun signal à haute confluence en ce moment. L'algorithme reste en veille.")}</div>
      </div>
    );
  }
  const isBuy = signal.direction === "ACHAT";
  const Icon = isBuy ? ArrowUpRight : ArrowDownRight;
  const blocked = signal.blocked;
  const dirLabel = isBuy ? t("signal.buy", "ACHAT") : t("signal.sell", "VENTE");
  return (
    <div className={`panel p-5 ${blocked ? "opacity-70" : "live-signal"}`} data-testid="signal-box">
      <div className="flex items-center justify-between mb-4">
        <div className="panel-title text-[14px]">{t("signal.title", "Signal actuel")}</div>
        <div className={`chip ${blocked ? "chip-neutral" : (isBuy ? "chip-up" : "chip-down")}`} data-testid="signal-direction">
          <Icon size={12} strokeWidth={2.5} /> {dirLabel}{blocked ? " (⛔)" : ""}
        </div>
      </div>
      {blocked && (
        <div className="mb-3 px-3 py-2 rounded-md bg-warn/10 border border-warn/30 text-warn text-xs" data-testid="signal-blocked">
          <div className="font-semibold mb-1">⛔ {t("signal.blocked", "Signal filtré par le contrôle qualité :")}</div>
          {(signal.blocked_reasons || []).map((r, i) => <div key={i}>• {r}</div>)}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <StatBlock label={t("signal.entry", "Entrée")} value={signal.entry} accent="text-white" testid="signal-entry" />
        <StatBlock label={t("signal.confidence", "Confiance")} value={`${signal.confidence}%`} accent="text-accent-blue" testid="signal-confidence" />
        <StatBlock label={t("signal.stop_loss", "Stop-Loss")} value={signal.stop_loss} accent="text-down" testid="signal-sl" />
        <StatBlock label={t("signal.take_profit", "Take-Profit")} value={signal.take_profit} accent="text-up" testid="signal-tp" />
        <StatBlock label={t("signal.rr", "Risk / Reward")} value={`1 : ${signal.risk_reward}`} accent="text-white" testid="signal-rr" />
        <StatBlock label="—" value={new Date(signal.generated_at).toLocaleTimeString()} accent="text-white/70" testid="signal-time" />
      </div>
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Confluences</div>
        {instScore && instScore.max > 0 && (
          <div
            className={`mb-2 inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border ${instScore.score === instScore.max ? "border-up/40 bg-up/10 text-up" : instScore.score === 0 ? "border-down/40 bg-down/10 text-down" : "border-white/10 bg-white/5 text-white/70"}`}
            data-testid="signal-inst-confluence"
          >
            🏛 Confluence institutionnelle : {instScore.score}/{instScore.max}
          </div>
        )}
        <ul className="text-xs text-white/70 space-y-1">
          {(signal.reasons || []).map((r, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-accent-blue">•</span> {r}
            </li>
          ))}
        </ul>
      </div>
      {signal.ml_prediction && (
        <div className="mb-4 p-3 rounded-md border border-accent-blue/30 bg-accent-blue/5" data-testid="ml-prediction">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] uppercase tracking-widest text-accent-blue">🤖 Prédiction ML</span>
            <span className="font-mono text-lg text-accent-blue">P(win) = {signal.ml_prediction.p_win}%</span>
          </div>
          <div className="text-xs text-white/70">{signal.ml_prediction.recommendation}</div>
          <div className="text-[10px] text-white/40 mt-1">EV : {signal.ml_prediction.expected_value_ratio} · Incertitude : {signal.ml_prediction.uncertainty_pct}%</div>
        </div>
      )}
      <button
        className={`btn ${blocked ? "" : "btn-primary"} w-full justify-center`}
        onClick={onNotify}
        disabled={notifying || blocked}
        data-testid="signal-notify-btn"
      >
        <Send size={14} /> {blocked ? "⛔" : (notifying ? t("common.loading", "…") : t("signal.notify", "M'alerter (Telegram + Email)"))}
      </button>
      {/* Regulatory footer — every displayed signal must remind that this is analytical output, not investment advice */}
      <div className="mt-3 text-[10px] text-white/40 leading-relaxed border-t border-white/5 pt-3" data-testid="signal-disclaimer">
        <b>Signal analytique</b> — configuration détectée par le modèle. Ne constitue ni un conseil en investissement
        au sens de MiFID II, ni une recommandation d&apos;achat/vente. Les performances passées ne préjugent pas des
        performances futures. Vous êtes seul décisionnaire de vos ordres.
      </div>
    </div>
  );
}

function StatBlock({ label, value, accent = "text-white", testid }) {
  return (
    <div className="bg-hover rounded-md px-3 py-2 border border-white/5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`font-mono text-[15px] ${accent} mt-1`}>{value}</div>
    </div>
  );
}
